import React, { useState, useEffect } from 'react';
import EmailTemplate from '../components/EmailTemplate';
import SmsTemplate from '../components/SmsTemplate';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { useParams } from 'react-router-dom';
import LogsTable from '../components/features/logs/LogsTable';
import { ArrowLeft } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import { useTheme } from '../context/ThemeContext';
import EmailKPIs from '../components/features/logs/EmailKPIs';
import PostalKPIs from '../components/features/logs/PostalKPIs';
import { toast } from 'react-toastify';
import AccessDeniedView from '../components/features/logs/AccessDeniedView';
import SubmissionPageHeader from '../components/features/logs/SubmissionPageHeader';
import LogActionButtons from '../components/features/logs/LogActionButtons';

const SubmissionTrackLogs = ({ isOpen, toggle }) => {
    const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
    const [selectedEmailData, setSelectedEmailData] = useState(null);
    const [isSmsPopupOpen, setIsSmsPopupOpen] = useState(false);
    const [selectedSmsData, setSelectedSmsData] = useState(null);
    const [postalDocUrl, setPostalDocUrl] = useState("");

    const handleOpenEmailPopup = (submission) => {
        const emailData = {
            subject: submission["Email Subject"] || 'No Subject',
            sentTo: submission["Email"],
            date: new Date(submission["createdAt"]).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
            body: submission["Sending Text"],
        };
        setSelectedEmailData(emailData);
        setIsEmailPopupOpen(true);
    };

    const handleOpenSmsPopup = (submission) => {
        const smsData = {
            message: submission["Sending Text"] || '',
            sentTo: submission["Phone"] || '',
            date: new Date(submission["createdAt"]).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
        };
        setSelectedSmsData(smsData);
        setIsSmsPopupOpen(true);
    };

    const handleCloseSmsPopup = () => {
        setIsSmsPopupOpen(false);
        setSelectedSmsData(null);
    };

    const handleCloseEmailPopup = () => {
        setIsEmailPopupOpen(false);
        setSelectedEmailData(null);
    };

    const handleOpenPostalPopup = () => {
        if (postalDocUrl) {
            window.open(postalDocUrl, '_blank', 'noopener,noreferrer');
        } else {
            alert('No postal document found.');
        }
    };

    const navigate = useNavigate();
    const { id } = useParams();
    const { jwtToken, accessToken, userTeamData } = useMicrosoftAuth();
    const { isDark } = useTheme();

    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [emailStats, setEmailStats] = useState(null);
    const [postalStats, setPostalStats] = useState(null);
    const [accessDenied, setAccessDenied] = useState(null);
    const fetchSubmissionLogs = async () => {
        try {
            const data = new FormData();
            data.append('submissionId', id);
            data.append('user_team_id', userTeamData?.id);
            data.append('user_role_id', userTeamData?.user_role_id);
            data.append('user_email', userTeamData?.email);
            userTeamData?.user_group?.forEach(group => {
                data.append('user_group[]', group);
            });

            if (userTeamData?.branch_manager_branches && userTeamData.branch_manager_branches.length > 0) {
                userTeamData.branch_manager_branches.forEach(branch => {
                    data.append('branch_manager_branches[]', branch.branch_id);
                });
            }

            const response = await api.post('/getUserSubmissionsTrackLogs',  data );
            const submissionsData = response.data.data;
            const emailStatsData = response.data.emailStats;
            const postalStatsData = response.data.postalStats;
            setSubmissions(submissionsData);
            setEmailStats(emailStatsData);
            setPostalStats(postalStatsData);
        } catch (error) {
            console.error('Error fetching submissions:', error);
            if (error.response?.status === 401) {
                toast.error('Your session has expired. Please login again.');
                navigate('/login');
            } else if (error.response?.status === 404) {
                setAccessDenied(error.response?.data?.message || 'Submission not found or access denied');
            } else {
                toast.error(error.response?.data?.message || 'Failed to fetch submission logs');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchPostalDocUrl = async () => {
            try {
                const token = jwtToken || localStorage.getItem('jwtToken') || accessToken || localStorage.getItem('msalToken');
                if (token) {
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                }
                const submissionId = id;
                const data = new FormData();
                data.append('user_team_id', userTeamData?.id);
                data.append('user_role_id', userTeamData?.user_role_id);
                data.append('user_email', userTeamData?.email);
                userTeamData?.user_group?.forEach(group => {
                    data.append('user_group[]', group);
                });
                const res = await api.post(`/submissions/${submissionId}`, data);
                const submissionDetail = res.data.data;
                const docUrl = submissionDetail.public_strata_url || submissionDetail.convertedPdfUrl || (submissionDetail.submission_recipients?.[0]?.public_strata_url) || '';
                setPostalDocUrl(docUrl);
            } catch (err) {
                setPostalDocUrl("");
            }
        };

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        fetchSubmissionLogs();
        fetchPostalDocUrl();
    }, []);

    const tableData = {
        headers: [
            "Comms Type",
            "Log Type",
            //"Is Success",
            "Plan No.",
            "Lot No.",
            "Unit No.",
            "Name",
            "Email",
            "Phone",
            "Notice Issue",
            "Type",
            // "Postal",
            // "Owner Occupied",
            // "Status",
            // "Sending Text",
            // "Email Subject",
            "Message",
            "Actions",
        ],
        data: submissions.map(submission => {
            // Format email status for email logs
            let messageContent = submission.message;
            if (submission.logType === 'email') {
                // Only show email status for successfully sent emails
                const isEmailSent = submission.isSuccess && submission.message && submission.message.toLowerCase().includes('successfully');

                if (isEmailSent) {
                    const sentDate = submission.createdAt ? new Date(submission.createdAt) : null;
                    const sentDateStr = sentDate
                        ? `${sentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Australia/Sydney' })} ${sentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Australia/Sydney' })}`
                        : null;
                    messageContent = (
                        <div className="flex flex-col gap-[8px]">
                            <div className="flex items-center gap-[6px]">
                                <span>{submission.message}</span>
                                {sentDateStr && (
                                    <span className={`text-[14px] font-semibold transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
                                        – {sentDateStr}
                                    </span>
                                )}
                            </div>
                            {submission.isSeen !== null && submission.firstSeenAt ? (
                                <div className="flex items-center gap-[6px]">
                                    <div
                                        className="w-[12px] h-[12px]"
                                        style={{
                                            backgroundColor: '#00A473',
                                            WebkitMaskImage: "url('/assets/icons/seenemails.svg')",
                                            WebkitMaskRepeat: 'no-repeat',
                                            WebkitMaskSize: 'contain',
                                            WebkitMaskPosition: 'center',
                                            maskImage: "url('/assets/icons/seenemails.svg')",
                                            maskRepeat: 'no-repeat',
                                            maskSize: 'contain',
                                            maskPosition: 'center',
                                        }}
                                        aria-hidden="true"
                                    />
                                   <span className="text-[14px] font-semibold" style={{ color: '#00A473' }}>
                                        Seen 
                                    </span>
                                    <span className={`text-[14px] font-semibold transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
                                       - {new Date(submission.firstSeenAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Australia/Sydney' })} {new Date(submission.firstSeenAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Australia/Sydney' })}
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-[6px]">
                                    <div
                                        className="w-[12px] h-[12px]"
                                        style={{
                                            backgroundColor: '#9A7B00',
                                            WebkitMaskImage: "url('/assets/icons/unseenemails.svg')",
                                            WebkitMaskRepeat: 'no-repeat',
                                            WebkitMaskSize: 'contain',
                                            WebkitMaskPosition: 'center',
                                            maskImage: "url('/assets/icons/unseenemails.svg')",
                                            maskRepeat: 'no-repeat',
                                            maskSize: 'contain',
                                            maskPosition: 'center',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <span className="text-[14px] font-semibold" style={{ color: '#9A7B00' }}>
                                        Unseen
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                }
            }

            return {
                "Comms Type": submission.processType,
                "Log Type": submission.logType,
                "Is Success": submission.isSuccess,
                "Plan No.": submission.planNumber,
                "Lot No.": submission.lotNumber,
                "Unit No.": submission.unitNumber,
                "Name": submission.name,
                "Email": submission.email,
                "Phone": submission.phone,
                "Notice Issue": submission.noticeIssue,
                "Type": submission.type == 'owner-agent' ? 'owner agent' : submission.type,
                // "Postal": submission.postal,
                // "Owner Occupied": submission.ownerOccupied,
                // "Status": submission.status,
                "Sending Text": submission.sendingText,
                "Email Subject": submission.emailSubject,
                "Message": messageContent,
                "createdAt": submission.createdAt,
                "Submission Id": submission.submissionId,
                "isSeen": submission.isSeen,
                "firstSeenAt": submission.firstSeenAt,
                "conversationId": submission.conversationId
            };
        })
    };
    const hasNonRawLogs = submissions.some(item => item.logType !== 'raw' && item.logType !== 'info');
    const hasRawUnsuccessLog = submissions.some(item => item.logType === 'raw' && !item.isSuccess);
    const infoLogs = tableData.data.filter(item => item["Log Type"] === "info");
    let filteredData = [];
    let useMinimalColumns = false;

    if (hasNonRawLogs) {
        filteredData = tableData.data.filter(item => item["Log Type"] !== "raw" && item["Log Type"] !== "info");
    } else if (hasRawUnsuccessLog) {
        filteredData = tableData.data.filter(item => item["Log Type"] === "raw" && !item["Is Success"]);
        useMinimalColumns = true;
    }

    // Always append info logs at the end
    filteredData = [...filteredData, ...infoLogs];

    // Apply search filter (case-insensitive search across all fields)
    if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredData = filteredData.filter(item => {
            return (
                item["Comms Type"]?.toString().toLowerCase().includes(query) ||
                item["Log Type"]?.toString().toLowerCase().includes(query) ||
                item["Plan No."]?.toString().toLowerCase().includes(query) ||
                item["Lot No."]?.toString().toLowerCase().includes(query) ||
                item["Unit No."]?.toString().toLowerCase().includes(query) ||
                item["Name"]?.toString().toLowerCase().includes(query) ||
                item["Email"]?.toString().toLowerCase().includes(query) ||
                item["Phone"]?.toString().toLowerCase().includes(query) ||
                item["Notice Issue"]?.toString().toLowerCase().includes(query) ||
                item["Type"]?.toString().toLowerCase().includes(query) ||
                item["Message"]?.toString().toLowerCase().includes(query)
            );
        });
    }

    if (accessDenied) {
        return <AccessDeniedView isDark={isDark} accessDenied={accessDenied} />;
    }

    return (
        <div className={`min-h-screen px-[16px] sm:px-[24px] py-[20px] sm:py-[30px] flex flex-col gap-[10px] sm:gap-[16px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-[#f5f5f5]'
        }`}>
            {isEmailPopupOpen && (
                <EmailTemplate
                    isOpen={isEmailPopupOpen}
                    onClose={handleCloseEmailPopup}
                    emailData={selectedEmailData}
                    isDark={isDark}
                />
            )}
            {isSmsPopupOpen && (
                <SmsTemplate
                    isOpen={isSmsPopupOpen}
                    onClose={handleCloseSmsPopup}
                    smsData={selectedSmsData}
                    isDark={isDark}
                />
            )}
            <SubmissionPageHeader id={id} isDark={isDark} title="Submission Logs" description="View your submission logs" />

            {/* KPIs Row */}
            {((emailStats && submissions.some(item => item.logType === 'email')) ||
              (postalStats && submissions.some(item => item.logType === 'postal'))) && (
                <div className="flex flex-col sm:flex-row gap-[16px] sm:gap-[20px] w-full">
                    {emailStats && submissions.some(item => item.logType === 'email') && (
                        <div className="sm:flex-[3]">
                            <EmailKPIs emailStats={emailStats} isDark={isDark} />
                        </div>
                    )}
                    {postalStats && submissions.some(item => item.logType === 'postal') && (
                        <div className="sm:flex-[2]">
                            <PostalKPIs postalStats={postalStats} isDark={isDark} />
                        </div>
                    )}
                </div>
            )}

            {/* Filters card*/}
            <div className={`border-solid border-[1px] rounded-[16px] sm:rounded-[24px] p-[16px] sm:p-[24px] flex flex-col gap-[12px] sm:gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
            }`}>
                <div className='flex items-center gap-[10px]'>
                    <span className={`text-[14px] sm:text-[16px] font-semibold transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#072837]'
                    }`}>Filters:</span>
                </div>
                <div className='flex items-center h-[40px]'>
                    <div className='w-full sm:w-[471px]'>
                        <SearchBar
                            value={searchQuery}
                            placeholder="Search for Logs"
                            onSearch={setSearchQuery}
                            disabled={loading}
                            variant="logs"
                            isDark={isDark}
                        />
                    </div>
                </div>
            </div>
            <div>
                {(() => {
                    const headersToUse = useMinimalColumns ? ["Message"] : tableData.headers
                    const dataWithActions = filteredData.map((item) => ({
                        ...item,
                        ...(useMinimalColumns
                          ? {}
                          : {
                              Actions: (
                                  <LogActionButtons
                                    item={item}
                                    isDark={isDark}
                                    postalDocUrl={postalDocUrl}
                                    onOpenEmailPopup={handleOpenEmailPopup}
                                    onOpenSmsPopup={handleOpenSmsPopup}
                                    onOpenPostalPopup={handleOpenPostalPopup}
                                  />
                                )
                            }),
                    }))
                    return (
                      <LogsTable headers={headersToUse} data={dataWithActions} title={'All Logs'} loading={loading} isDark={isDark} centerAlign={useMinimalColumns} />
                    )
                })()}
            </div>
        </div>
    );
};

export default SubmissionTrackLogs;

